import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    BarChart2, Users, CheckCircle2, Circle, Tag, LogOut,
    RefreshCw, Search, ChevronRight, Wifi,
    LayoutDashboard, List, Layers, Lock, Mail, Eye, EyeOff,
    TrendingUp, UserCheck, Clock, Award, X
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const BASE = `${API}/api/custom-events/admin/dashboard`;
const STORAGE_KEY = 'kapconConfDashAuth';

// ── Login ──────────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
    const [form, setForm] = useState({ username: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showPw, setShowPw] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/login`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            });
            const data = await res.json();
            if (data.success) {
                const auth = { username: form.username, password: form.password, role: data.role || 'full', eventInfo: { name: data.eventName } };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
                onLogin(auth);
            } else setError(data.message || 'Invalid credentials');
        } catch { setError('Cannot connect to server'); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-600 mb-4 shadow-lg shadow-indigo-200">
                        <LayoutDashboard className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">KAPCON 2026</h1>
                    <p className="text-sm text-gray-500 mt-1">Conference Dashboard</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Username</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type="text" required value={form.username}
                                    onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
                                    className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    placeholder="admin-username" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input type={showPw ? 'text' : 'password'} required value={form.password}
                                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                                    className="w-full pl-10 pr-10 py-2.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                <button type="button" onClick={() => setShowPw(!showPw)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {error && (
                            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                <X className="w-3.5 h-3.5 text-red-500 shrink-0" />
                                <p className="text-xs text-red-600">{error}</p>
                            </div>
                        )}
                        <button type="submit" disabled={loading}
                            className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-lg disabled:opacity-50 transition-colors">
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

// ── KPI Card ───────────────────────────────────────────────────────────────────
const KPI = ({ label, value, sub, icon: Icon, color }) => {
    const cfg = {
        indigo: { bg: 'bg-indigo-50',  icon: 'text-indigo-600', border: 'border-indigo-100' },
        green:  { bg: 'bg-emerald-50', icon: 'text-emerald-600', border: 'border-emerald-100' },
        amber:  { bg: 'bg-amber-50',   icon: 'text-amber-600',  border: 'border-amber-100' },
        slate:  { bg: 'bg-gray-100',   icon: 'text-gray-500',   border: 'border-gray-200' },
    }[color || 'indigo'];
    return (
        <div className={`rounded-xl border ${cfg.border} bg-white p-4 flex items-start gap-3 shadow-sm`}>
            <div className={`p-2 rounded-lg ${cfg.bg} shrink-0`}>
                <Icon className={`w-4 h-4 ${cfg.icon}`} />
            </div>
            <div className="min-w-0">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider truncate">{label}</p>
                <p className="text-2xl font-black text-gray-900 mt-0.5 leading-none">{value}</p>
                {sub && <p className="text-[11px] text-gray-400 mt-1">{sub}</p>}
            </div>
        </div>
    );
};

// ── Progress Bar ───────────────────────────────────────────────────────────────
const Bar = ({ value, max, color = 'indigo' }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    const colors = { indigo: 'bg-indigo-500', green: 'bg-emerald-500', amber: 'bg-amber-400' };
    return (
        <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                <div className={`${colors[color]} h-1.5 rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
            </div>
            <span className="text-[10px] text-gray-400 font-mono w-8 text-right">{pct}%</span>
        </div>
    );
};

// ── Overview Tab ───────────────────────────────────────────────────────────────
const OverviewTab = ({ credentials, onStats, showEmpty }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [lastRefresh, setLastRefresh] = useState(null);
    const timerRef = useRef(null);

    const fetch_ = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const res = await fetch(`${BASE}/stats`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const data = await res.json();
            if (data.success) {
                setStats(data.data);
                setLastRefresh(new Date());
                onStats?.(data.data);
            }
        } catch {}
        finally { setLoading(false); }
    }, [credentials, onStats]);

    useEffect(() => {
        fetch_();
        timerRef.current = setInterval(() => fetch_(true), 5000);
        return () => clearInterval(timerRef.current);
    }, [fetch_]);

    if (loading && !stats) return (
        <div className="flex items-center justify-center h-full">
            <RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" />
        </div>
    );
    if (!stats) return null;

    const categories = stats.categories || [];
    const uncategorized = stats.uncategorized || { total: 0, checkedIn: 0 };
    const allCats = [
        ...categories,
        ...(uncategorized.total > 0
            ? [{ id: '__none__', name: 'Uncategorized', total: uncategorized.total, checkedIn: uncategorized.checkedIn }]
            : [])
    ];

    return (
        <div className="h-full flex gap-3">
            {/* LEFT: Big live check-in counter */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center px-6"
                style={{ flex: '0 0 20%' }}>
                <div className="flex items-center gap-2 mb-4">
                    <span className="flex h-3.5 w-3.5 relative">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-emerald-500" />
                    </span>
                    <span className="text-base font-extrabold text-gray-700 uppercase tracking-widest">Live Check-ins</span>
                </div>
                <span className="font-black text-gray-900 tracking-tighter leading-none"
                    style={{ fontSize: 'clamp(6rem, 11vw, 14rem)' }}>
                    {stats.checkedIn || 0}
                </span>
                {lastRefresh && (
                    <span className="text-xs text-gray-400 flex items-center gap-1 mt-5">
                        <Wifi className="w-3 h-3" /> {lastRefresh.toLocaleTimeString()}
                    </span>
                )}
            </div>

            {/* RIGHT: Category grid */}
            <div className="flex-1 min-h-0 flex flex-col gap-2">
                <p className="text-sm font-extrabold text-gray-600 uppercase tracking-widest shrink-0 pl-0.5">
                    Check-ins by Category
                </p>
                <div className="flex-1 min-h-0 grid gap-2"
                    style={{
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gridAutoRows: 'minmax(0, 1fr)'
                    }}>
                    {allCats.filter(cat => cat.total >= 1 && (showEmpty ? true : cat.checkedIn > 0)).map(cat => {
                        const pct = Math.round((cat.checkedIn / cat.total) * 100);
                        return (
                            <div key={cat.id}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center text-center px-3 py-2 overflow-hidden">
                                <p className="text-xs font-extrabold text-gray-600 uppercase tracking-wide leading-tight line-clamp-2 mb-1">
                                    {cat.name}
                                </p>
                                <span className="font-black text-gray-900 tracking-tighter leading-none"
                                    style={{ fontSize: 'clamp(2.5rem, 4vw, 5rem)' }}>
                                    {cat.checkedIn}
                                </span>
                                <span className="text-sm font-semibold text-gray-500 mt-1">/ {cat.total}</span>
                                <div className="w-full bg-gray-100 rounded-full mt-2" style={{ height: '4px' }}>
                                    <div className="bg-emerald-500 rounded-full h-full transition-all duration-700"
                                        style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-xs font-bold text-gray-500 mt-1">{pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

// ── Participant Table Row ──────────────────────────────────────────────────────
const PRow = ({ p, fields, expanded, onToggle }) => {
    const displayName = p.data.fullName || p.data.name || 'Unknown';

    return (
        <>
            <tr onClick={onToggle} className="cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100">
                <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                        {p.isCheckedIn
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                            : <Circle className="w-3.5 h-3.5 text-gray-300 shrink-0" />}
                        <span className="text-sm font-semibold text-gray-800 truncate max-w-[160px]">{displayName}</span>
                    </div>
                </td>
                <td className="px-4 py-2.5 text-[11px] font-mono text-gray-400 hidden sm:table-cell">{p.registrationId || '—'}</td>
                <td className="px-4 py-2.5 hidden md:table-cell">
                    {p.categoryName
                        ? <span className="px-2 py-0.5 text-[10px] font-bold bg-indigo-50 text-indigo-600 rounded-md">{p.categoryName}</span>
                        : <span className="text-gray-300 text-[10px]">—</span>}
                </td>
                <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md ${
                        p.paymentStatus === 'completed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>{p.paymentStatus}</span>
                </td>
                <td className="px-4 py-2.5 text-gray-300">
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </td>
            </tr>
            {expanded && (
                <tr className="bg-gray-50 border-b border-gray-100">
                    <td colSpan={5} className="px-6 py-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2">
                            {fields.map(f => {
                                const val = p.data[f.id];
                                if (!val && val !== 0) return null;
                                return (
                                    <div key={f.id}>
                                        <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{f.fieldName}</p>
                                        <p className="text-xs text-gray-700 mt-0.5">{String(val)}</p>
                                    </div>
                                );
                            })}
                        </div>
                        {p.isCheckedIn && p.checkedInAt && (
                            <p className="text-[10px] text-emerald-600 mt-2 flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" />
                                Checked in at {new Date(p.checkedInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

// ── Participant Table ──────────────────────────────────────────────────────────
const ParticipantTable = ({ participants, fields }) => {
    const [expandedId, setExpandedId] = useState(null);
    return (
        <div className="overflow-x-auto rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden sm:table-cell">Reg ID</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider hidden md:table-cell">Category</th>
                        <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-2.5 w-8"></th>
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {participants.map(p => (
                        <PRow key={p._id} p={p} fields={fields}
                            expanded={expandedId === p._id}
                            onToggle={() => setExpandedId(expandedId === p._id ? null : p._id)} />
                    ))}
                    {participants.length === 0 && (
                        <tr><td colSpan={5} className="text-center py-10 text-gray-400 text-sm">No participants found</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// ── All Participants Tab ───────────────────────────────────────────────────────
const AllTab = ({ credentials }) => {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [checkinFilter, setCheckinFilter] = useState('all');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/participants`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const json = await res.json();
            if (json.success) setRaw(json);
        } catch {}
        finally { setLoading(false); }
    }, [credentials]);

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" /></div>;
    if (!raw) return null;

    const filtered = raw.data.filter(p => {
        if (checkinFilter === 'in' && !p.isCheckedIn) return false;
        if (checkinFilter === 'out' && p.isCheckedIn) return false;
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return Object.values(p.data).join(' ').toLowerCase().includes(q)
            || (p.registrationId || '').toLowerCase().includes(q)
            || (p.categoryName || '').toLowerCase().includes(q);
    });

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-2 items-center">
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Search name, reg ID, category..."
                        className="w-full pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                    {search && (
                        <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>
                <div className="flex gap-0.5 bg-gray-100 border border-gray-200 rounded-lg p-0.5">
                    {[['all', 'All'], ['in', 'Checked In'], ['out', 'Pending']].map(([v, l]) => (
                        <button key={v} onClick={() => setCheckinFilter(v)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                                checkinFilter === v ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}>{l}</button>
                    ))}
                </div>
                <button onClick={load} className="p-2 bg-white border border-gray-200 rounded-lg text-gray-400 hover:text-gray-600 transition-colors">
                    <RefreshCw className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-400 font-mono">{filtered.length} results</span>
            </div>
            <ParticipantTable participants={filtered} fields={raw.participantFields} />
        </div>
    );
};

// ── By Category Tab ────────────────────────────────────────────────────────────
const ByCatTab = ({ credentials }) => {
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState(null);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${BASE}/participants`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(credentials)
            });
            const json = await res.json();
            if (json.success) {
                setRaw(json);
                if (json.participantCategories.length > 0 && !activeCat)
                    setActiveCat(json.participantCategories[0].id);
            }
        } catch {}
        finally { setLoading(false); }
    }, [credentials]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    if (loading) return <div className="flex justify-center py-32"><RefreshCw className="w-6 h-6 text-indigo-500 animate-spin" /></div>;
    if (!raw) return null;
    if (raw.participantCategories.length === 0)
        return <p className="text-center text-gray-400 py-20">No categories found</p>;

    const grouped = {};
    for (const cat of raw.participantCategories) grouped[cat.id] = [];
    grouped['__none__'] = [];
    for (const p of raw.data) {
        const key = p.categoryId && grouped[p.categoryId] !== undefined ? p.categoryId : '__none__';
        grouped[key].push(p);
    }

    const allTabs = [
        ...raw.participantCategories,
        ...(grouped['__none__'].length > 0 ? [{ id: '__none__', name: 'Uncategorized' }] : [])
    ];

    const catParticipants = grouped[activeCat] || [];
    const filtered = catParticipants.filter(p => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return Object.values(p.data).join(' ').toLowerCase().includes(q)
            || (p.registrationId || '').toLowerCase().includes(q);
    });
    const checkedCount = catParticipants.filter(p => p.isCheckedIn).length;

    return (
        <div className="space-y-4">
            <div className="flex gap-1.5 overflow-x-auto pb-1">
                {allTabs.map(cat => {
                    const g = grouped[cat.id] || [];
                    const cin = g.filter(p => p.isCheckedIn).length;
                    const active = activeCat === cat.id;
                    return (
                        <button key={cat.id}
                            onClick={() => { setActiveCat(cat.id); setSearch(''); }}
                            className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                                active
                                    ? 'bg-indigo-600 border-indigo-600 text-white'
                                    : 'bg-white border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                            }`}>
                            {cat.name}
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${
                                active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                            }`}>{cin}/{g.length}</span>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-3 gap-3">
                <KPI label="Registered" value={catParticipants.length} icon={Users} color="indigo" />
                <KPI label="Checked In" value={checkedCount} icon={CheckCircle2} color="green" />
                <KPI label="Pending" value={catParticipants.length - checkedCount} icon={Clock} color="amber" />
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search in this category..."
                    className="w-full pl-9 pr-8 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                {search && (
                    <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>

            <ParticipantTable participants={filtered} fields={raw.participantFields} />
        </div>
    );
};

// ── Main ───────────────────────────────────────────────────────────────────────
const TABS = [
    { id: 'overview',  label: 'Overview',    Icon: BarChart2 },
    { id: 'all',       label: 'Participants', Icon: List      },
    { id: 'category',  label: 'By Category', Icon: Layers    },
];

const ConferenceDashboard = () => {
    const [auth, setAuth] = useState(null);
    const [tab, setTab] = useState('overview');
    const [navStats, setNavStats] = useState(null);
    const [showEmpty, setShowEmpty] = useState(false);

    useEffect(() => {
        try { const s = localStorage.getItem(STORAGE_KEY); if (s) setAuth(JSON.parse(s)); } catch {}
    }, []);

    if (!auth) return <LoginScreen onLogin={a => setAuth(a)} />;

    const creds = { username: auth.username, password: auth.password };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0">
                            <LayoutDashboard className="w-4 h-4 text-white" />
                        </div>
                        <div>
                            <span className="font-bold text-sm text-gray-900">KAPCON 2026</span>
                            <span className="ml-2 text-[10px] text-gray-400">@{auth.username}</span>
                        </div>
                        {navStats && (
                            <div className="hidden sm:flex items-center gap-1.5 ml-2 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-lg">
                                <Users className="w-3 h-3 text-indigo-400" />
                                <span className="text-xs font-black text-indigo-700">{navStats.total}</span>
                                <span className="text-[10px] text-indigo-400 font-medium">registered</span>
                            </div>
                        )}
                    </div>

                    <nav className="hidden sm:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                        {TABS.map(({ id, label, Icon }) => (
                            <button key={id} onClick={() => setTab(id)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                    tab === id
                                        ? 'bg-white text-indigo-600 shadow-sm'
                                        : 'text-gray-500 hover:text-gray-700'
                                }`}>
                                <Icon className="w-3.5 h-3.5" />{label}
                            </button>
                        ))}
                    </nav>

                    {tab === 'overview' && (
                        <div className="hidden sm:flex items-center gap-2">
                            <span className="text-xs text-gray-400 font-medium">Show all</span>
                            <button
                                onClick={() => setShowEmpty(v => !v)}
                                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${showEmpty ? 'bg-indigo-600' : 'bg-gray-200'}`}>
                                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform duration-200 ${showEmpty ? 'translate-x-4' : 'translate-x-0'}`} />
                            </button>
                        </div>
                    )}

                    <button
                        onClick={() => { localStorage.removeItem(STORAGE_KEY); setAuth(null); }}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors">
                        <LogOut className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Logout</span>
                    </button>
                </div>

                <div className="sm:hidden flex border-t border-gray-100">
                    {TABS.map(({ id, label, Icon }) => (
                        <button key={id} onClick={() => setTab(id)}
                            className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold transition-colors ${
                                tab === id
                                    ? 'text-indigo-600 border-t-2 border-indigo-500 -mt-px bg-indigo-50/50'
                                    : 'text-gray-400'
                            }`}>
                            <Icon className="w-4 h-4" />{label}
                        </button>
                    ))}
                </div>
            </header>

            <main className="px-4 py-3 overflow-hidden" style={{ height: 'calc(100vh - 56px)' }}>
                {tab === 'overview' && <OverviewTab credentials={creds} onStats={setNavStats} showEmpty={showEmpty} />}
                {tab === 'all'      && <div className="h-full overflow-y-auto"><AllTab credentials={creds} /></div>}
                {tab === 'category' && <div className="h-full overflow-y-auto"><ByCatTab credentials={creds} /></div>}
            </main>
        </div>
    );
};

export default ConferenceDashboard;
